import { exportJobDocxApi } from "@/api/services/main/job";
import { IconFileExport, IconInfoOutline } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Modal } from "@/components/elements/Modal";
import { RadioGroup, TRadioOption } from "@/components/elements/RadioGroup";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { text } from "@/theme/colors";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { FC, Key, useState } from "react";
import { toast } from "@/utils/toast";
import { JOB_EXPORT_FAILED_MESSAGE } from "@/features/job/constants/common";

type FieldMaskValue = "full" | "partial";
type CompanionLanguageValue = "none" | "vi" | "id" | "my" | "en" | "ne";

type JobExportOptionsValue = {
  fieldMask: FieldMaskValue;
  companionLanguage: CompanionLanguageValue;
};

const FIELD_MASK_OPTIONS: TRadioOption[] = [
  { value: "full", label: "全ての求人情報" },
  { value: "partial", label: "一部マスキングした求人情報" },
];

const COMPANION_LANGUAGE_OPTIONS: TRadioOption[] = [
  { value: "none", label: "なし" },
  { value: "vi", label: "ベトナム語" },
  { value: "id", label: "インドネシア語" },
  { value: "my", label: "ミャンマー語" },
  { value: "en", label: "英語" },
  { value: "ne", label: "ネパール語" },
];

const DEFAULT_VALUE: JobExportOptionsValue = {
  fieldMask: "full",
  companionLanguage: "none",
};

// Tooltip content (provisional — may be refined later by product)
const TOOLTIP_FIELD_MASK =
  "マスキング（空欄）する求人情報は、紹介料（売上）、紹介料（仕入）、求人獲得ルート、顧客区分、求人担当者コメントになります。";
const TOOLTIP_COMPANION_LANGUAGE =
  "マスキング（空欄）する求人情報は、紹介料（売上）、紹介料（仕入）、求人獲得ルート、顧客区分、求人担当者コメントになります。";

type Props = {
  modal: UseVisibleReturn;
  jobId?: string;
  jobTitle?: string | null;
};

const SectionLabel: FC<{ label: string; tooltip: string }> = ({ label, tooltip }) => (
  <Stack direction="row" alignItems="center" gap={0.5}>
    <Typography variant="cap12" color={text.secondary}>
      {label}
    </Typography>
    <Tooltip
      title={
        <Typography variant="body14" whiteSpace="pre-line">
          {tooltip}
        </Typography>
      }
      placement="top"
    >
      <Box sx={{ display: "flex", alignItems: "center", cursor: "help" }}>
        <IconInfoOutline width={16} height={16} />
      </Box>
    </Tooltip>
  </Stack>
);

export const JobExportOptionsModal: FC<Props> = ({ modal, jobId, jobTitle }) => {
  const [value, setValue] = useState<JobExportOptionsValue>(DEFAULT_VALUE);
  const [isExporting, setIsExporting] = useState(false);

  const update =
    <K extends keyof JobExportOptionsValue>(key: K) =>
    (v: Key) => {
      setValue((prev) => ({ ...prev, [key]: v as JobExportOptionsValue[K] }));
    };

  const handleClose = () => {
    if (isExporting) return;
    setValue(DEFAULT_VALUE);
    modal.close();
  };

  const handleExport = async () => {
    if (!jobId || isExporting) return;
    setIsExporting(true);
    try {
      const blob = await exportJobDocxApi(jobId, { mask: value.fieldMask, lang: value.companionLanguage });
      // Multilang exports get a "_母語併記" suffix so users can tell which language
      // version a downloaded file is (per IROHANA_MATCH_DEV-392 feedback).
      // Pulls the JA label straight from COMPANION_LANGUAGE_OPTIONS to stay in
      // sync with the radio UI without a separate mapping table.
      const langLabel =
        value.companionLanguage !== "none" ? COMPANION_LANGUAGE_OPTIONS.find((opt) => opt.value === value.companionLanguage)?.label : undefined;
      const langSuffix = langLabel ? `_${langLabel}併記` : "";
      const fileName = `${jobTitle ?? "求人票"}の求人票${langSuffix}.docx`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      setValue(DEFAULT_VALUE);
      modal.close();
    } catch {
      toast.error(JOB_EXPORT_FAILED_MESSAGE);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal isOpen={modal.visible} onClose={handleClose} width={800} title="求人票を出力" enableFooter={false}>
      <Stack gap={3} sx={{ mt: 1 }}>
        <Typography variant="cap12" color={text.secondary}>
          パートナーに共有する求人票をこちらから出力できます。
        </Typography>

        <Stack gap={1}>
          <SectionLabel label="項目名" tooltip={TOOLTIP_FIELD_MASK} />
          <RadioGroup nonControl direction="row" value={value.fieldMask} options={FIELD_MASK_OPTIONS} onChange={update("fieldMask")} />
        </Stack>

        <Stack gap={1}>
          <SectionLabel label="併記する言語" tooltip={TOOLTIP_COMPANION_LANGUAGE} />
          <RadioGroup
            nonControl
            direction="row"
            value={value.companionLanguage}
            options={COMPANION_LANGUAGE_OPTIONS}
            onChange={update("companionLanguage")}
          />
        </Stack>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
          <Button size="sm" variant="ghost" onClick={handleClose} disabled={isExporting}>
            キャンセル
          </Button>
          <Button size="sm" startIcon={<IconFileExport />} onClick={handleExport} isLoading={isExporting}>
            出力
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
